"use client";

// L'accueil : l'espace de travail de l'entreprise, dans le même atelier qu'un projet (Atelier.tsx). Les agents de
// l'entreprise à gauche, le chat ouvert, puis trois onglets : les projets en cours, le Socle, la base que
// l'entreprise reprend dans chaque mémoire, et la Marketplace, des appels d'offres à prendre. Ces agents écrivent le Socle et lisent les projets ; ils se parlent entre
// eux et parlent aux agents des projets. Au début d'un tour, l'accueil garde l'état du Socle : chaque écriture de
// l'agent (effet « kanon.updated », portée « entreprise ») recharge la vue, et ce que le tour a changé est marqué.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  api,
  ENTREPRISE,
  ErreurApi,
  EVENEMENTS,
  type AffaireResume,
  type Annonce,
  type Config,
  type EtatObjectif,
  type Entreprise,
  type Evenement,
  type Fil,
} from "@/lib/api";
import { NOM_BASE, ROLES } from "@/lib/textes";
import { Atelier, BarreNavigation, ouvrirChat, useNavigation } from "./Atelier";
import type { ChatHandle } from "./Chat";
import { aTrancher, Projets, SqueletteProjets } from "./Projets";
import { Marketplace, SqueletteMarketplace } from "./Marketplace";
import {
  CarteAgentOrchestrateur,
  EnteteOrchestrateur,
  useOrchestrateur,
} from "./Orchestrateur";
import { EnDirect, useOuvrirFilDeProjet } from "./EnDirect";
import { CarteAgentVeille, EnteteVeille, useVeille } from "./Veille";
import { ArbreSocle, Socle } from "./Socle";
import { SqueletteArbre, SqueletteDocument, Trait } from "./Squelette";

type Onglet = "projets" | "socle" | "marketplace";
// Dans l'adresse : /?onglet=marketplace, /?onglet=socle (rien pour les projets).
const ONGLETS: readonly Onglet[] = ["projets", "socle", "marketplace"];
const LIBELLES: Record<Onglet, string> = {
  projets: "Projets",
  socle: NOM_BASE,
  marketplace: "Marketplace",
};

// La liste des projets suit les nouvelles de l'API (`/api/evenements`) : un document enregistré, un agent qui
// commence ou finit un tour, un projet pris. Plusieurs nouvelles rapprochées ne font qu'une relecture.
const GROUPER_NOUVELLES = 400;
// Au cas où le flux se serait tu sans qu'on le sache : un coup d'œil par minute.
const RAFRAICHIR_PROJETS = 60000;
// Les documents qui changent une ligne de projet : sa pipeline, son mémoire, son analyse, ses fils.
const GENRES_PROJET = [
  "affaires",
  "analyses",
  "memoires",
  "arbitrages",
  "avancements",
  "taches",
  "sessions",
];
// Le temps où la ligne d'un projet qu'on vient de prendre reste éclairée.
const NOUVEAU = 8000;

const texteErreur = (e: unknown) =>
  e instanceof ErreurApi ? e.message : "L'API ne répond pas.";

export function Accueil() {
  const [config, setConfig] = useState<Config | null>(null);
  const [projets, setProjets] = useState<AffaireResume[] | null>(null);
  const [filsProjets, setFilsProjets] = useState<Record<string, Fil[]>>({});
  const [objectifs, setObjectifs] = useState<Record<
    string,
    EtatObjectif
  > | null>(null);
  const [entreprise, setEntreprise] = useState<Entreprise | null>(null);
  const [erreurSocle, setErreurSocle] = useState<string | null>(null);
  const [avant, setAvant] = useState<Entreprise | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [agentActif, setAgentActif] = useState(false);
  const [rubrique, setRubrique] = useState<string | null>(null);
  const [annonces, setAnnonces] = useState<Annonce[] | null>(null);
  const [nouveaux, setNouveaux] = useState<string[]>([]);
  const { nav, allerA, precedent, suivant } = useNavigation<Onglet>(
    "projets",
    ONGLETS,
  );
  const chat = useRef<ChatHandle>(null);
  const entrepriseRef = useRef(entreprise);
  entrepriseRef.current = entreprise;

  const chargerSocle = useCallback(async () => {
    try {
      setEntreprise(await api.entreprise());
      setErreurSocle(null);
    } catch (e) {
      setErreurSocle(
        e instanceof ErreurApi && e.status === 404
          ? `Le ${NOM_BASE} n'est pas encore disponible : l'API doit être relancée.`
          : texteErreur(e),
      );
    }
  }, []);

  const chargerProjets = useCallback(async () => {
    try {
      const liste = await api.affaires();
      setProjets(liste);
      const fils = await Promise.all(
        liste.map((p) =>
          api.fils({ genre: "projet", id: p.id }).catch(() => [] as Fil[]),
        ),
      );
      setFilsProjets(Object.fromEntries(liste.map((p, i) => [p.id, fils[i]])));
      // L'orchestrateur : null si l'API ne l'a pas (une version d'avant), la page fait sans.
      const objectifs = await api.objectif().catch(() => null);
      setObjectifs(
        objectifs && Object.fromEntries(objectifs.map((o) => [o.affaire, o])),
      );
    } catch (e) {
      setErreur(texteErreur(e));
    }
  }, []);

  const chargerAnnonces = useCallback(async () => {
    try {
      setAnnonces(await api.marketplace());
    } catch (e) {
      setErreur(texteErreur(e));
    }
  }, []);

  useEffect(() => {
    api
      .config()
      .then(setConfig)
      .catch((e) => setErreur(texteErreur(e)));
    void chargerProjets();
    void chargerSocle();
    void chargerAnnonces();
  }, [chargerProjets, chargerSocle, chargerAnnonces]);

  // Un projet qu'on vient de prendre : sa ligne s'éclaire un moment.
  const marquerNouveau = useCallback((id: string) => {
    setNouveaux((n) => (n.includes(id) ? n : [...n, id]));
    window.setTimeout(
      () => setNouveaux((n) => n.filter((x) => x !== id)),
      NOUVEAU,
    );
  }, []);

  // Qui travaille sur quel projet, et où en est sa pipeline : en direct, par les nouvelles de l'API.
  useEffect(() => {
    let attente: number | undefined;
    const relire = () => {
      window.clearTimeout(attente);
      attente = window.setTimeout(
        () => void chargerProjets(),
        GROUPER_NOUVELLES,
      );
    };
    const flux = new EventSource(EVENEMENTS);
    flux.onmessage = (m) => {
      const e = JSON.parse(m.data) as Evenement;
      if (e.type === "affaire") {
        marquerNouveau(e.affaire);
        void chargerAnnonces();
        relire();
      } else if (e.type === "sourcing" && e.action === "passe") {
        void chargerAnnonces();
      } else if (
        e.type === "agent" ||
        e.type === "decision" ||
        e.type === "orchestrateur" ||
        (e.type === "document" && GENRES_PROJET.includes(e.genre))
      ) {
        relire();
      }
    };
    const minuterie = window.setInterval(() => {
      if (window.document.visibilityState === "visible") void chargerProjets();
    }, RAFRAICHIR_PROJETS);
    return () => {
      flux.close();
      window.clearTimeout(attente);
      window.clearInterval(minuterie);
    };
  }, [chargerProjets, chargerAnnonces, marquerNouveau]);

  // L'orchestrateur : le relancer (un projet, ou tous ceux qui ne sont pas prêts), ou mettre un projet en pause.
  const relancer = useCallback(
    async (affaire?: string) => {
      try {
        await api.relancer(affaire);
      } catch (e) {
        setErreur(texteErreur(e));
      }
      await chargerProjets();
    },
    [chargerProjets],
  );
  const pauseProjet = useCallback(
    async (affaire: string, pause: boolean) => {
      try {
        await api.pauseProjet(affaire, pause);
      } catch (e) {
        setErreur(texteErreur(e));
      }
      await chargerProjets();
    },
    [chargerProjets],
  );

  // Prendre une annonce : l'API importe le DCE et lance l'orchestrateur ; on va voir le projet arriver.
  const prendre = useCallback(
    async (id: string) => {
      await api.prendre(id);
      marquerNouveau(id);
      allerA("projets");
      await Promise.all([chargerProjets(), chargerAnnonces()]);
    },
    [allerA, chargerProjets, chargerAnnonces, marquerNouveau],
  );

  const onDebut = useCallback(() => {
    setAvant(entrepriseRef.current);
    setAgentActif(true);
  }, []);

  const onFin = useCallback(() => {
    setAgentActif(false);
    void chargerSocle();
    void chargerProjets();
  }, [chargerSocle, chargerProjets]);

  const onEffet = useCallback(
    (nom: string, data?: Record<string, unknown>) => {
      if (nom !== "kanon.updated") return;
      if (data?.portee === "entreprise") void chargerSocle();
      else if (typeof data?.affaire === "string") void chargerProjets();
    },
    [chargerSocle, chargerProjets],
  );

  // Ce que l'utilisateur enregistre lui-même ne compte pas comme un changement de l'agent.
  const onEnregistre = useCallback(async () => {
    setAvant(null);
    await chargerSocle();
  }, [chargerSocle]);

  // Une demande à l'agent rouvre le volet du chat s'il est fermé, puis remplit le compositeur.
  const demander = useCallback(
    (texte: string) =>
      void ouvrirChat().then(() => chat.current?.proposer(texte)),
    [],
  );

  // L'Orchestrateur, toujours là : épinglé en tête des agents ; l'en-tête de son fil porte ses commandes.
  const {
    o: orch,
    maintenant,
    reveiller,
    activer,
  } = useOrchestrateur(setErreur);
  const ouvrirFilDeProjet = useOuvrirFilDeProjet();
  const epingle = useMemo(
    () =>
      orch && {
        fil: orch.fil,
        carte: (occupe: boolean) => (
          <CarteAgentOrchestrateur
            o={orch}
            maintenant={maintenant}
            occupe={occupe}
          />
        ),
        entete: (
          <EnteteOrchestrateur
            o={orch}
            maintenant={maintenant}
            onReveiller={reveiller}
            onActiver={activer}
          />
        ),
        direct: orch.au_travail.map((t) => (
          <EnDirect
            key={t.fil}
            fil={t.fil}
            titre={t.mission}
            sousTitre={t.objet}
            onOuvrir={() => ouvrirFilDeProjet(t.affaire, t.fil)}
          />
        )),
      },
    [orch, maintenant, reveiller, activer, ouvrirFilDeProjet],
  );

  // Le Veilleur, épinglé sous lui : il surveille les appels d'offres ; l'en-tête de son fil ouvre la Marketplace.
  const veille = useVeille();
  // « Discuter avec le Veilleur » d'une annonce de la Marketplace : son fil s'ouvre, le compositeur nomme l'annonce.
  const filVeille = veille?.fil;
  const discuter = useCallback(
    (texte: string) => {
      if (!filVeille) return;
      void ouvrirChat()
        .then(() => chat.current?.ouvrir(filVeille))
        .then(() => chat.current?.proposer(texte));
    },
    [filVeille],
  );
  const epingleVeille = useMemo(
    () =>
      veille && {
        fil: veille.fil,
        classe: "veille",
        carte: (occupe: boolean) => (
          <CarteAgentVeille v={veille} occupe={occupe} />
        ),
        entete: (
          <EnteteVeille
            v={veille}
            occupe={veille.occupe}
            onMarketplace={() => allerA("marketplace")}
          />
        ),
        direct: null,
      },
    [veille, allerA],
  );

  // L'accueil s'affiche tout de suite : chaque donnée qui n'est pas encore là a son trait à sa place.
  const nom = entreprise?.nom ?? (erreurSocle ? "L'entreprise" : null);
  const fiches =
    entreprise?.rubriques.reduce((n, r) => n + r.fiches.length, 0) ?? 0;
  // Les décisions qui attendent l'entreprise, tous projets confondus : la pastille de l'onglet Projets.
  const aTrancherTotal = (projets ?? []).reduce((n, p) => n + aTrancher(p), 0);

  return (
    <Atelier
      portee={ENTREPRISE}
      config={config}
      sansPanneau={nav.onglet === "projets" || nav.onglet === "marketplace"}
      entete={
        <>
          <span className="marque-accueil" aria-label="minibreek">
            <svg className="logo-briques" viewBox="0 0 26 20" aria-hidden>
              <rect className="ocre" x="2" y="1" width="15" height="5" rx="2" />
              <rect
                className="rouge"
                x="8"
                y="7.5"
                width="15"
                height="5"
                rx="2"
              />
              <rect
                className="noire"
                x="2"
                y="14"
                width="15"
                height="5"
                rx="2"
              />
            </svg>
            <span className="nom" aria-hidden>
              <span className="mini">mini</span>breek
            </span>
          </span>
        </>
      }
      erreur={
        erreur && !config
          ? `${erreur} La lancer avec « make api » (port 8100).`
          : erreur
      }
      onErreur={setErreur}
      titreFils="Agents"
      epingle={[epingle, epingleVeille]}
      roles={ROLES}
      poignee={chat}
      chapitre={null}
      onEffet={onEffet}
      onDebut={onDebut}
      onFin={onFin}
      libellePanneau={
        nav.onglet === "socle"
          ? "les rubriques"
          : nav.onglet === "marketplace"
            ? "les annonces"
            : "la liste des projets"
      }
      navigation={
        <BarreNavigation
          nav={nav}
          precedent={precedent}
          suivant={suivant}
          libelle="Navigation entre les onglets"
          actions={
            // L'entreprise elle-même (son Socle), au bout de la barre : ce qu'elle sait d'elle-même.
            <div className="onglets onglets-fin">
              <button
                className={nav.onglet === "socle" ? "actif" : ""}
                onClick={() => allerA("socle")}
                title={
                  entreprise
                    ? `Le ${NOM_BASE} de ${entreprise.nom} : ${fiches} fiche${fiches > 1 ? "s" : ""}`
                    : `Le ${NOM_BASE}`
                }
              >
                {nom ?? <Trait largeur="5em" />}
              </button>
            </div>
          }
        >
          {(["projets", "marketplace"] as Onglet[]).map((o) => (
            <button
              key={o}
              className={o === nav.onglet ? "actif" : ""}
              onClick={() => allerA(o)}
            >
              {o === "projets" && projets
                ? `${LIBELLES[o]} (${projets.length})`
                : o === "marketplace" && annonces
                  ? `${LIBELLES[o]} (${annonces.filter((a) => !a.pris).length})`
                  : LIBELLES[o]}
              {o === "projets" && aTrancherTotal > 0 && (
                <span
                  className="notif"
                  title={`${aTrancherTotal} arbitrage${aTrancherTotal > 1 ? "s" : ""} à trancher`}
                >
                  {aTrancherTotal}
                </span>
              )}
            </button>
          ))}
        </BarreNavigation>
      }
      document={
        <>
          {nav.onglet === "projets" &&
            (projets ? (
              <Projets
                projets={projets}
                fils={filsProjets}
                objectifs={objectifs}
                nouveaux={nouveaux}
                onDemander={demander}
                onNouveau={() => allerA("marketplace")}
                onRelancer={relancer}
                onPause={pauseProjet}
                orchestrateurActif={orch?.actif ?? true}
              />
            ) : (
              <SqueletteProjets />
            ))}
          {nav.onglet === "socle" &&
            (entreprise ? (
              <Socle
                entreprise={entreprise}
                avant={avant}
                agentActif={agentActif}
                onVue={setRubrique}
                onEnregistre={onEnregistre}
                onDemander={demander}
              />
            ) : erreurSocle ? (
              <div className="vide">
                <p>{erreurSocle}</p>
              </div>
            ) : (
              <SqueletteDocument libelle={`Chargement du ${NOM_BASE}`} />
            ))}
          {nav.onglet === "marketplace" &&
            (annonces ? (
              <Marketplace
                annonces={annonces}
                onPrendre={prendre}
                onDiscuter={filVeille ? discuter : undefined}
              />
            ) : (
              <SqueletteMarketplace />
            ))}
        </>
      }
      panneau={
        <>
          {nav.onglet === "socle" && !entreprise && !erreurSocle && (
            <SqueletteArbre />
          )}
          {nav.onglet === "socle" && entreprise && (
            <ArbreSocle
              entreprise={entreprise}
              avant={avant}
              rubrique={rubrique}
            />
          )}
        </>
      }
    />
  );
}
